import { Button, Grid, Paper, Typography } from "@mui/material";
import React from "react";
import Chart from "react-apexcharts";
import { Link } from "react-router-dom";

/**
 * Lokale Nachbauten der frueher aus @inventurefinancial/frontend-components
 * bezogenen Komponenten. Nur noch vom Legacy-/portal-Bereich verwendet.
 */

type DonutChartProps = {
  headline: string;
  series: number[];
  labels?: string[];
  theme?: any;
  style?: React.CSSProperties;
};

export function DonutChart({ headline, series, labels, style }: DonutChartProps) {
  const total = series.reduce((a, b) => a + b, 0);
  const options: any = {
    chart: { type: "donut", background: "transparent" },
    labels: labels || [],
    legend: { position: "bottom" },
    dataLabels: { enabled: false },
    plotOptions: {
      pie: {
        donut: {
          size: "72%",
          labels: {
            show: true,
            total: {
              show: true,
              label: "Gesamtwert",
              formatter: () => total.toLocaleString("de-DE") + " €",
            },
            value: {
              formatter: (val: any) =>
                Number(val).toLocaleString("de-DE") + " €",
            },
          },
        },
      },
    },
    tooltip: {
      y: { formatter: (val: number) => val.toLocaleString("de-DE") + " €" },
    },
  };

  return (
    <div style={style}>
      <Typography variant="h6" style={{ marginBottom: "10px" }}>
        <b>{headline}</b>
      </Typography>
      <Chart options={options} series={series} type="donut" width="100%" />
    </div>
  );
}

type TransactionItem = {
  target: string;
  type: number;
  date: string;
  amount: number;
  transaction_url?: string;
  documents?: { name: string; url: string }[];
  theme?: any;
};

type TransactionListProps = {
  transactions: TransactionItem[];
  theme?: any;
};

export function TransactionList({ transactions }: TransactionListProps) {
  if (transactions.length === 0)
    return (
      <Typography variant="body2" color="textSecondary">
        Keine Transaktionen vorhanden
      </Typography>
    );

  return (
    <div>
      {transactions.map((transaction, index) => {
        const content = (
          <Grid
            container
            alignItems="center"
            style={{ padding: "12px 16px" }}
            spacing={1}
          >
            <Grid item xs={12} sm={4}>
              <Typography variant="body1" style={{ fontWeight: 600 }}>
                {transaction.target}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {transaction.type === 1 ? "Co-Investment" : "Fondsinvestment"}
              </Typography>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Typography variant="body2" color="textSecondary">
                {transaction.date}
              </Typography>
            </Grid>
            <Grid item xs={6} sm={2}>
              <Typography variant="body2" align="right">
                {transaction.amount.toLocaleString("de-DE")} €
              </Typography>
            </Grid>
            <Grid item xs={12} sm={3}>
              {transaction.documents?.map((doc) => (
                <a
                  key={doc.url}
                  href={doc.url}
                  target="_blank"
                  rel="noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  style={{ display: "block", fontSize: "13px" }}
                >
                  {doc.name}
                </a>
              ))}
            </Grid>
          </Grid>
        );

        return (
          <Paper
            key={index}
            variant="outlined"
            style={{ marginBottom: "10px" }}
          >
            {transaction.transaction_url ? (
              <Link
                to={transaction.transaction_url}
                style={{ textDecoration: "none", color: "inherit" }}
              >
                {content}
              </Link>
            ) : (
              content
            )}
          </Paper>
        );
      })}
    </div>
  );
}

type SimpleActionBannerProps = {
  headline: string;
  button_text?: string;
  button_link?: string;
  theme?: any;
  style?: React.CSSProperties;
  children?: React.ReactNode;
};

export function SimpleActionBanner({
  headline,
  button_text,
  button_link,
  style,
  children,
}: SimpleActionBannerProps) {
  return (
    <Paper variant="outlined" style={{ padding: "20px", ...style }}>
      <Grid container alignItems="center" spacing={2}>
        <Grid item xs={12} sm>
          <Typography variant="h6" style={{ fontWeight: 700 }}>
            {headline}
          </Typography>
          <Typography variant="body2" color="textSecondary">
            {children}
          </Typography>
        </Grid>
        {button_text && button_link && (
          <Grid item xs={12} sm="auto">
            <Button
              variant="contained"
              color="primary"
              component={Link}
              to={button_link}
            >
              {button_text}
            </Button>
          </Grid>
        )}
      </Grid>
    </Paper>
  );
}
